import React from "react";
import PropTypes from "prop-types";
import { Paper, TableContainer, Table as TableM } from "@mui/material";
import { withApi, withNotification } from "wrappers";
import { DEFAULT_MUI_DATATABLE_SIZE } from "constantes/constants";
import { isFunction } from "utils/functions";
import Head from "./Head";
import Body from "./Body";
import BodySelect from "./select/Body";
import Pagination from "./Pagination";
import BodyExpandable from "./expandable/Body";
import ToolBar from "./ToolBar";
import CatalogView from "./CatalogView";
import { withTheme } from '@mui/styles';

/* Para actualizar la tabla serverside
 * ------ En el componente padre -------
 * -> Crear la referencia
 *    this.child = React.createRef();
 * -> Enviar referencia por props
 *    <TableGeneral forwardedRef={this.child} />
 * -> Para ejecutar el metodo refrescar
 *    this.child.current.refresh();
 */
class Table extends React.Component {
  constructor(props) {
    super(props);

    // Inicializar columnas visibles
    const { columns, defaultSorts } = props;
    const initialVisibleColumns = columns
      ? columns.filter(col => col.name).map(col => col.name)
      : [];

    this.state = {
      isLoading: true,
      page: 0,
      rowsPerPage: 50,
      sorts: defaultSorts || {}, // Usar defaultSorts si está definido
      total: 0,
      data: [],
      selected: [],
      filters: {},
      visibleColumns: initialVisibleColumns,
      columnWidths: {},
      viewMode: props.defaultViewMode || 'list', // 'list' o 'catalog'
    };
    const { forwardedRef } = this.props;
    if (forwardedRef) {
      forwardedRef.current = {
        refresh: this.serverSide,
        addRow: this.addRow,
        removeRow: this.removeRow,
        updateRow: this.updateRow,
        getData: () => this.state.data,
      };
    }
  }

  componentDidMount() {
    const { serverSideUrl } = this.props;
    if (serverSideUrl) {
      this.serverSide();
    } else {
      this.processData();
      this.setState({ isLoading: false });
    }
  }

  // Funcion para evaluar cambios en los datos
  componentDidUpdate(prevProps) {
    const { data, serverSideUrl, serverSideData } = this.props;
    if (prevProps.data !== data && !serverSideUrl) {
      this.processData();
    }
    if (prevProps.selectedRowIds !== this.props.selectedRowIds) {
      this.syncSelectedRows(this.state.data);
    }
    if (
      serverSideUrl &&
      JSON.stringify(prevProps.serverSideData || {}) !== JSON.stringify(serverSideData || {})
    ) {
      this.setState({ page: 0, isLoading: true }, this.serverSide);
    }
  }

  // Funcion para cambiar los filtros
  setFilter = (filters) => {
    if (this.props.serverSideUrl) {
      this.setState({ filters, page: 0, isLoading: true }, this.serverSide);
    } else {
      const data = this.filterData(filters);
      this.setState({ data, page: 0 });
    }
  };

  // Funcion para cambiar de pagina
  setPage = (page) => {
    if (this.props.serverSideUrl) {
      this.setState({ page, isLoading: true }, this.serverSide);
    } else {
      this.setState({ page });
    }
  };

  // Funcion para cambiar los registros por pagina
  setRowsPerPage = (rowsPerPage) => {
    if (this.props.serverSideUrl) {
      this.setState({ rowsPerPage, page: 0, isLoading: true }, this.serverSide);
    } else {
      this.setState({ rowsPerPage, page: 0 });
    }
  };

  // Función para manejar ordenamiento múltiple
  setSort = (columnName) => {
    const { sorts } = this.state;
    const currentOrder = sorts[columnName];

    // Ciclo: asc -> desc -> eliminar
    let newOrder;
    if (!currentOrder) {
      newOrder = 'asc';
    } else if (currentOrder === 'asc') {
      newOrder = 'desc';
    } else {
      newOrder = null; // Eliminar ordenamiento
    }

    const newSorts = { ...sorts };

    if (newOrder) {
      newSorts[columnName] = newOrder;
    } else {
      delete newSorts[columnName];
    }

    if (this.props.serverSideUrl) {
      this.setState({ sorts: newSorts, isLoading: true }, this.serverSide);
    } else {
      const data = this.orderDataMultiple(newSorts);
      this.setState({ sorts: newSorts, data });
    }
  };

  // Función para eliminar un ordenamiento específico
  removeSort = (columnName) => {
    const { sorts } = this.state;
    const newSorts = { ...sorts };
    delete newSorts[columnName];

    if (this.props.serverSideUrl) {
      this.setState({ sorts: newSorts, isLoading: true }, this.serverSide);
    } else {
      const data = this.orderDataMultiple(newSorts);
      this.setState({ sorts: newSorts, data });
    }
  };

  // Función para eliminar todos los ordenamientos
  clearAllSorts = () => {
    if (this.props.serverSideUrl) {
      this.setState({ sorts: {}, isLoading: true }, this.serverSide);
    } else {
      const data = this.orderDataMultiple({});
      this.setState({ sorts: {}, data });
    }
  };

  // Función para cambiar la visibilidad de las columnas
  setColumnVisibility = (visibleColumns) => {
    this.setState({ visibleColumns });
  };

  // Función para cambiar el ancho de las columnas
  setColumnWidth = (columnIndex, width) => {
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [columnIndex]: width
      }
    }));
  };

  // Función para cambiar el modo de vista
  setViewMode = (viewMode) => {
    this.setState({ viewMode });
  };

  // Método para agregar una fila manualmente
  addRow = (row) => {
    this.setState(prevState => ({
      data: [row, ...prevState.data],
      total: prevState.total + 1
    }));
  };

  // Método para eliminar una fila manualmente
  removeRow = (rowId) => {
    this.setState(prevState => ({
      data: prevState.data.filter(row => row.id !== rowId),
      total: prevState.total - 1
    }));
  };

  // Método para actualizar parcialmente una fila existente
  updateRow = (rowId, partial) => {
    this.setState(prevState => ({
      data: prevState.data.map(row => row.id === rowId ? { ...row, ...partial } : row)
    }));
  };

  // Funcion para filtrar los elementos segun parametros de busqueda
  // TODO: Hacer filtro de datos normales
  // https://www.w3schools.com/jsref/jsref_includes.asp
  filterData = (filters) => {
    const { data: originalData } = this.props;
    let newData = [...originalData];

    Object.entries(filters).forEach(([columnName, filterValue]) => {
      if (Array.isArray(filterValue) && filterValue.length > 0) {

        newData = newData.filter((row) =>
          filterValue.includes(String(row[columnName]))
        );
      } else if (typeof filterValue === 'string' && filterValue !== '') {

        newData = newData.filter((row) =>
          String(row[columnName]).toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    });

    return newData;
  };

  // Función para ordenar múltiples columnas
  orderDataMultiple = (sorts) => {
    const { data } = this.props;
    const sortEntries = Object.entries(sorts);

    if (sortEntries.length === 0) {
      return data;
    }

    const orderedData = [...data].sort((a, b) => {
      for (const [columnName, order] of sortEntries) {
        const ascending = order === "asc" ? 1 : -1;
        const aValue = a[columnName];
        const bValue = b[columnName];

        if (aValue < bValue) {
          return -1 * ascending;
        }
        if (aValue > bValue) {
          return 1 * ascending;
        }
      }
      return 0;
    });

    return orderedData;
  };

  // Función que filtra solo datos segun pagina y registros por pagina
  pageData = (data) => {
    if (this.props.serverSideUrl) {
      return data;
    }

    const { page, rowsPerPage } = this.state;
    const initialRow = page * rowsPerPage;
    return data.slice(initialRow, initialRow + rowsPerPage);
  };

  // Funcion encargada de cargar los datos solicitados segun parametros
  serverSide = async () => {
    const { serverSideUrl, serverSideData, doGet, onDataLoaded } = this.props;
    const { page, rowsPerPage, sorts, filters } = this.state;

    const params = {
      url: serverSideUrl,
      data: {
        perPage: rowsPerPage,
        page: page + 1,
        sorts: JSON.stringify(sorts),
        filters: JSON.stringify(filters),
        ...serverSideData,
      },
    };

    try {
      const resp = await doGet(params);

      const payload = resp?.response ?? resp;
      let data = [];
      let total = 0;

      if (Array.isArray(payload?.data)) {
        data = payload.data;
        total = payload.total ?? payload.meta?.total ?? payload.pagination?.total ?? payload.data.length;
      } else if (Array.isArray(payload?.data?.data)) {
        data = payload.data.data;
        total = payload.data.total ?? payload.data.meta?.total ?? payload.data.pagination?.total ?? payload.data.data.length;
      } else if (Array.isArray(payload?.response?.data)) {
        data = payload.response.data;
        total = payload.response.total ?? payload.response.meta?.total ?? payload.response.pagination?.total ?? payload.response.data.length;
      } else {
        data = payload?.data || [];
        total = payload?.total || 0;
      }
      if (!Array.isArray(data)) {
        data = [];
        total = 0;
      }
      this.setState(
        {
          data,
          total: total || 0,
          isLoading: false,
        },
        () => {
          this.syncSelectedRows(this.state.data);
          if (isFunction(onDataLoaded)) {
            onDataLoaded(resp);
          }
        }
      );

    } catch (error) {
      this.props.appError(
        "Ocurrio un error cargando los datos de la tabla",
        error
      );
    }
  };

  verifyExist = (row) => {
    const { selected } = this.state;
    let index = -1;
    selected.forEach((element, i) => {
      if (Object.is(element, row)) {
        index = i;
      }
    });
    return index;
  };

  handleSelectAllClick = (event) => {
    let newSelecteds = [];
    const { data } = this.state;
    const { onRowsSelect, selectedRowIds, rowIdKey = 'id' } = this.props;

    if (event.target.checked) {
      newSelecteds = data.map((row) => row);
    }

    if (Array.isArray(selectedRowIds)) {
      let nextIds;
      if (event.target.checked) {
        const ids = new Set(selectedRowIds);
        data.forEach((row) => ids.add(row[rowIdKey]));
        nextIds = Array.from(ids);
      } else {
        // En modo controlado (selectedRowIds), desmarcar desde cabecera limpia toda la selección.
        nextIds = [];
      }

      this.setState({ selected: newSelecteds }, () =>
        onRowsSelect(this.state.selected, nextIds)
      );
      return;
    }

    this.setState({ selected: newSelecteds }, () => onRowsSelect(this.state.selected));
  };

  setCheck = (row) => {
    const { onRowsSelect, singleSelect, selectedRowIds, rowIdKey = 'id' } = this.props;
    const index = this.verifyExist(row);

    if (Array.isArray(selectedRowIds)) {
      const ids = new Set(selectedRowIds);
      const id = row[rowIdKey];
      if (ids.has(id)) ids.delete(id);
      else {
        if (singleSelect) ids.clear();
        ids.add(id);
      }
      const nextIds = Array.from(ids);

      if (index >= 0) {
        this.setState(
          (prevState) => {
            const nextSelected = [...prevState.selected];
            nextSelected.splice(index, 1);
            return { selected: nextSelected };
          },
          () => onRowsSelect(this.state.selected, nextIds)
        );
      } else {
        this.setState(
          (prevState) => {
            const selected = singleSelect ? [row] : [...prevState.selected, row];
            return { selected };
          },
          () => onRowsSelect(this.state.selected, nextIds)
        );
      }
      return;
    }

    if (index >= 0) {
      this.setState(
        (prevState) => {
          const nextSelected = [...prevState.selected];
          nextSelected.splice(index, 1);
          return { selected: nextSelected };
        },
        () => onRowsSelect(this.state.selected)
      );
    } else {
      this.setState(
        (prevState) => {
          const selected = singleSelect ? [row] : [...prevState.selected, row];
          return { selected };
        },
        () => onRowsSelect(this.state.selected)
      );
    }
  };

  // Funcion que llama las correspondientes funciones segun el caso
  processData = () => {
    const { sorts } = this.state;
    const orderData = this.orderDataMultiple(sorts);
    this.setState({ data: orderData, total: orderData.length }, () =>
      this.syncSelectedRows(this.state.data)
    );
  };

  syncSelectedRows = (data) => {
    const { selectedRowIds, rowIdKey = 'id' } = this.props;
    if (!Array.isArray(selectedRowIds)) return;
    const selected = data.filter((row) => selectedRowIds.includes(row[rowIdKey]));
    this.setState({ selected });
  };

  render() {
    const {
      columns,
      title,
      onCreate,
      createPermissions,
      onRowsSelect,
      ExtraToolBar,
      paperProps,
      disableCreateButton,
      disableToolbar,
      expandable,
      stripe,
      showViewToggle,
    } = this.props;
    const {
      page,
      rowsPerPage,
      sorts,
      data,
      total,
      filters,
      isLoading,
      selected,
      viewMode,
    } = this.state;

    return (
      <Paper {...paperProps}>
        {!disableToolbar ? (
          <ToolBar
            title={title}
            columns={columns}
            filters={filters}
            sorts={sorts}
            setFilter={this.setFilter}
            onCreate={onCreate}
            createPermissions={createPermissions}
            ExtraToolBar={ExtraToolBar}
            disableCreateButton={disableCreateButton || false}
            serverSideUrl={this.props.serverSideUrl}
            onExportStart={this.props.onExportStart}
            onExportComplete={this.props.onExportComplete}
            onExportError={this.props.onExportError}
            visibleColumns={this.state.visibleColumns}
            onColumnVisibilityChange={this.setColumnVisibility}
            columnWidths={this.state.columnWidths}
            onColumnWidthChange={this.setColumnWidth}
            onSortChange={this.setSort}
            onRemoveSort={this.removeSort}
            onClearAllSorts={this.clearAllSorts}
            viewMode={viewMode}
            onViewModeChange={this.setViewMode}
            showViewToggle={showViewToggle}
          />
        ) : (
          ""
        )}
        {viewMode === 'catalog' ? (
          <CatalogView
            columns={columns}
            data={this.pageData(data)}
            isLoading={isLoading}
            selected={selected}
            onRowsSelect={onRowsSelect}
            setCheck={this.setCheck}
            verifyExist={this.verifyExist}
            visibleColumns={this.state.visibleColumns}
          />
        ) : (
          <TableContainer>
            <TableM size={DEFAULT_MUI_DATATABLE_SIZE}>
              <Head
                columns={columns}
                sorts={sorts}
                setSort={this.setSort}
                extraColumn={Boolean(onRowsSelect)}
                expandable={Boolean(expandable)}
                handleSelectAllClick={this.handleSelectAllClick}
                selected={selected}
                totalRow={total}
                selectedRowIds={this.props.selectedRowIds}
                filters={filters}
                setFilter={this.setFilter}
                data={data}
                visibleColumns={this.state.visibleColumns}
                columnWidths={this.state.columnWidths}
                onColumnWidthChange={this.setColumnWidth}
              />
              {Boolean(expandable) && (
                <BodyExpandable
                  columns={columns}
                  data={this.pageData(data)}
                  isLoading={isLoading}
                  expandable={expandable}
                  visibleColumns={this.state.visibleColumns}
                  columnWidths={this.state.columnWidths}
                />
              )}
              {Boolean(onRowsSelect) && (
                <BodySelect
                  columns={columns}
                  data={this.pageData(data)}
                  isLoading={isLoading}
                  onRowsSelect={onRowsSelect}
                  setCheck={this.setCheck}
                  verifyExist={this.verifyExist}
                  visibleColumns={this.state.visibleColumns}
                  columnWidths={this.state.columnWidths}
                />
              )}
              {!onRowsSelect && !expandable && (
                <Body
                  columns={columns}
                  data={this.pageData(data)}
                  isLoading={isLoading}
                  stripe={stripe}
                  visibleColumns={this.state.visibleColumns}
                  columnWidths={this.state.columnWidths}
                  rowStyle={this.props.rowStyle}
                />
              )}
            </TableM>
          </TableContainer>
        )}
        <Pagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          setPage={this.setPage}
          setRowsPerPage={this.setRowsPerPage}
        />
      </Paper>
    );
  }
}

Table.defaultProps = {
  data: [],
  paperProps: {},
};

Table.propTypes = {
  // permite especificar las props para el paper que envuelve a esta tabla
  paperProps: PropTypes.shape(),
  // Array de columnas, cada columna puede tener varias llaves
  // label: Es el texto que se quiere mostrar en el Toobar de la tabla
  // name: Texto que identificara datos a poner en cada columna y registros a buscar
  // align: alineacion de las columnas left, rigth, center, por defecto es left
  // component: React component o función, en caso de ser funcion pasa como parametro el registro
  // filter: booleano para filtrar registros, por defecto true
  // width: tamaño de la columna
  // type: tipo de columna, por el momento se acepta 'number'
  columns: PropTypes.oneOfType([PropTypes.array]).isRequired,
  // Array de objetos para mostrar en en la tabla
  data: PropTypes.arrayOf(PropTypes.object),
  // Titulo de la tabla
  title: PropTypes.string,
  // Url a la cual se buscaran los datos
  serverSideUrl: PropTypes.string,
  // Data para la url en caso de necesitar enviar algun parametro extra
  serverSideData: PropTypes.oneOfType([PropTypes.object]),
  // Funcion para crear nuevos registros
  onCreate: PropTypes.func,
  // Funcion para expandir la columna, devuelve como parametro el registro
  expandable: PropTypes.func,
  // Funcion para retornar la data que llega del servidor
  onDataLoaded: PropTypes.func,
  // Funcion que devuelve las columnas seleccionadas
  onRowsSelect: PropTypes.func,
  // Booleano para seleccionar solo un registro
  singleSelect: PropTypes.bool,
  // Elemento para crear nuevos botones que se deseen adicionar en la parte superior de la tabla.
  ExtraToolBar: PropTypes.node,
  // Permisos para el boton de crear
  createPermissions: PropTypes.string,
  doGet: PropTypes.func,
  appError: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([PropTypes.object]),
  // Prop para habilitar o deshabilitar el boton de create.
  disableCreateButton: PropTypes.bool,
  // Props para habilitar o deshabilitar el toolbar
  disableToolbar: PropTypes.bool,
  // Ordenamiento por defecto al inicializar la tabla
  defaultSorts: PropTypes.object,
  // Para table de selección si deseo deshabilitar el Checkbox para una row en especifico,
  // en el data se debe enviar el campo "selectable" con valor booleano para
  // habilitar o deshabilitar.
  stripe: PropTypes.bool,
  // Función para personalizar el estilo de las filas (recibe la fila como parámetro y retorna un objeto de estilos)
  rowStyle: PropTypes.func,
  // Props para exportación
  onExportStart: PropTypes.func,
  onExportComplete: PropTypes.func,
  onExportError: PropTypes.func,
  // Prop para mostrar/ocultar el botón de cambio de vista
  showViewToggle: PropTypes.bool,
  // Prop para establecer el modo de vista por defecto
  defaultViewMode: PropTypes.oneOf(['list', 'catalog']),
  // Prop para seleccionar filas por id
  selectedRowIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  // Prop para definir la llave del id en los datos
  rowIdKey: PropTypes.string,
};

export default withApi(withNotification(withTheme(Table)));
